import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSoundscape } from './audio/useSoundscape';
import { IslandConnection, type ConnectionStatus } from './network';
import type { CollectResult, MoveMsg, PublicPlayer, SceneContext, SelfState } from '../shared/types';
import { PICK_MS } from '../shared/world';

const Scene = lazy(() => import('./scene/Scene').then(module => ({ default: module.Scene })));

class WorldBoundary extends Component<{ children: ReactNode; preview: boolean; onExit(): void; onError(): void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError(); }
  render() { return this.state.failed ? this.props.preview ? <div className="preview-fallback"/> : <div className="world-error" role="alert"><h2>해변을 불러오지 못했어요</h2><p>인터넷 연결과 브라우저의 3D 지원을 확인한 뒤 새로고침해 주세요.</p><button autoFocus onClick={this.props.onExit}>새로고침하고 다시 입장하기</button></div> : this.props.children; }
}
function Icon({ kind }: { kind: 'bag' | 'sound' | 'muted' | 'close' }) {
  return <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {kind === 'bag' ? <><path d="M8 4h8l-2 4c6 4 8 12-2 12S4 12 10 8L8 4Z"/><path d="M9 8h6"/></> : kind === 'close' ? <path d="m6 6 12 12M18 6 6 18"/> : <><path d="M11 5 6 9H3v6h3l5 4V5Z"/>{kind === 'sound' ? <><path d="M15 8c3 2 3 6 0 8M18 5c5 4 5 10 0 14"/></> : <path d="m16 9 6 6m0-6-6 6"/>}</>}
  </svg>;
}
export function App() {
  const sound = useSoundscape();
  const [screen, setScreen] = useState<'landing' | 'nickname' | 'world'>('landing');
  const [nickname, setNickname] = useState('');
  const [nameError, setNameError] = useState('');
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [message, setMessage] = useState('');
  const [players, setPlayers] = useState<PublicPlayer[]>([]);
  const [localId, setLocalId] = useState<string | null>(null);
  const [self, setSelf] = useState<SelfState | null>(null);
  const [context, setContext] = useState<SceneContext>({ kind: 'none' });
  const [pending, setPending] = useState<number | null>(null);
  const [pickLocked, setPickLocked] = useState(false);
  const [worldFailed, setWorldFailed] = useState(false);
  const [sceneFailed, setSceneFailed] = useState(false);
  const [bag, setBag] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [toast, setToast] = useState('');
  const connection = useRef<IslandConnection | null>(null);
  const reqId = useRef(0);
  const pendingRef = useRef<number | null>(null);
  const shownResults = useRef(new Set<string>());
  const lockTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const entryButton = useRef<HTMLButtonElement>(null);
  const returning = useRef(false);
  const nameInput = useRef<HTMLInputElement>(null);
  const bagButton = useRef<HTMLButtonElement>(null);
  const audioButton = useRef<HTMLButtonElement>(null);
  const clearPending = useCallback(() => { pendingRef.current = null; setPending(null); }, []);
  const receiveResult = useCallback((result: CollectResult) => {
    if (pendingRef.current === result.reqId) clearPending();
    setSelf(old => old ? { ...old, count: Math.max(old.count, result.count) } : old);
    if (result.ok && !result.duplicate && !shownResults.current.has(result.shellId)) {
      shownResults.current.add(result.shellId); setToast('행운의조개 +1');
      setPickLocked(true); clearTimeout(lockTimer.current);
      lockTimer.current = setTimeout(() => setPickLocked(false), PICK_MS);
    } else if (!result.ok) {
      const reasons = { too_far: '조개에 조금 더 가까이 다가가 주세요.', busy: '동작을 마친 뒤 다시 주워 주세요.', not_found: '새로운 조개가 도착했어요.', invalid: '수집 상태를 다시 확인했어요. 다시 시도해 주세요.' };
      setToast(reasons[result.reason ?? 'invalid']);
    }
  }, [clearPending]);
  const enterWorld = (event: React.FormEvent) => {
    event.preventDefault();
    const name = nickname.trim().normalize('NFC');
    if (/[\p{Cc}\p{Cf}]/u.test(name) || [...name].length < 1 || [...name].length > 12) { setNameError('닉네임을 제어 문자 없이 1~12자로 입력해 주세요.'); nameInput.current?.focus(); return; }
    if (sceneFailed) { setWorldFailed(true); setScreen('world'); return; }
    sound.start(); connection.current?.close();
    setNameError(''); setSelf(null); setPlayers([]); setLocalId(null); setContext({ kind: 'none' }); setMessage(''); setToast(''); setBag(false); setAudioOpen(false); setPickLocked(false); setWorldFailed(false); clearPending(); shownResults.current.clear(); reqId.current = 0;
    setScreen('world');
    const next = new IslandConnection({
      status: (value, text) => { setStatus(value); setMessage(text ?? ''); if (value !== 'connected') clearPending(); },
      players: (value, id) => { setPlayers(value); setLocalId(id); },
      self: state => { setSelf(old => old && old.count > state.count ? old : state); clearPending(); },
      result: receiveResult, actionError: setToast,
    });
    connection.current = next; void next.join(name);
  };
  const exit = useCallback(() => {
    sound.stop(); connection.current?.close(); connection.current = null; clearTimeout(lockTimer.current); clearPending(); setPickLocked(false); setWorldFailed(false); setLocalId(null); setPlayers([]); setSelf(null); setScreen('nickname'); setBag(false); setAudioOpen(false);
  }, [clearPending, sound.stop]);
  const restartAfterWorldError = useCallback(() => {
    exit();
    // A new document clears rejected GLTF and lazy-module promises as well as
    // the memory-only session. Re-entering inside this document cannot do that.
    window.location.reload();
  }, [exit]);
  const onWorldError = useCallback(() => {
    setSceneFailed(true);
    if (screen !== 'world') return;
    connection.current?.close(); connection.current = null; sound.stop();
    clearTimeout(lockTimer.current); clearPending(); setPickLocked(false);
    setWorldFailed(true); setContext({ kind: 'none' }); setBag(false); setAudioOpen(false); setToast('');
  }, [clearPending, sound.stop, screen]);
  useEffect(() => {
    const hide = () => connection.current?.close();
    const show = (event: PageTransitionEvent) => { if (event.persisted) location.reload(); };
    window.addEventListener('pagehide', hide); window.addEventListener('pageshow', show);
    return () => { window.removeEventListener('pagehide', hide); window.removeEventListener('pageshow', show); connection.current?.close(); clearTimeout(lockTimer.current); };
  }, []);
  useEffect(() => {
    if (screen === 'nickname') nameInput.current?.focus();
    if (screen === 'landing' && returning.current) { entryButton.current?.focus(); returning.current = false; }
  }, [screen]);
  useEffect(() => { if (!toast) return; const timer = setTimeout(() => setToast(''), 3000); return () => clearTimeout(timer); }, [toast]);
  useEffect(() => {
    if (pending === null) return;
    const timer = setTimeout(() => { clearPending(); connection.current?.sync(); setToast('수집 상태를 확인했어요. 필요하면 다시 눌러 주세요.'); }, 5000);
    return () => clearTimeout(timer);
  }, [pending, clearPending]);
  const onMove = useCallback((m: MoveMsg) => { if (!worldFailed) connection.current?.move(m); }, [worldFailed]);
  const onContext = useCallback((c: SceneContext) => setContext(old => JSON.stringify(old) === JSON.stringify(c) ? old : c), []);
  const disabled = worldFailed || status !== 'connected' || !self || pending !== null || pickLocked || bag || audioOpen;
  const act = useCallback(() => {
    if (disabled) return;
    if (context.kind === 'collect') {
      const id = ++reqId.current; pendingRef.current = id; setPending(id);
      connection.current?.collect({ reqId: id, shellId: context.shellId });
    } else if (context.kind === 'standUp') connection.current?.act({ kind: 'stop' });
    else if (context.kind === 'rest') connection.current?.act({ kind: 'lie', spotId: context.spotId });
  }, [disabled, context]);
  useEffect(() => {
    if (screen !== 'world') return;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (bag) { setBag(false); bagButton.current?.focus(); } else if (audioOpen) { setAudioOpen(false); audioButton.current?.focus(); } return; }
      if (e.code === 'KeyE' && !e.repeat && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLElement && e.target.isContentEditable)) { e.preventDefault(); act(); }
    };
    window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key);
  }, [screen, act, bag, audioOpen]);

  const preview = screen !== 'world';
  return <main className={preview ? 'world entry' : 'world'} aria-label="우소섬">
    <WorldBoundary preview={preview} onExit={restartAfterWorldError} onError={onWorldError}>
      <Suspense fallback={preview ? <div className="preview-fallback"/> : <div className="scene-loading" role="status" aria-live="polite">해변을 불러오고 있어요…</div>}>
        <Scene mode={preview ? 'preview' : 'world'} localId={localId} players={players} self={self} disabled={disabled} onMove={onMove} onContext={onContext}/>
      </Suspense>
    </WorldBoundary>
    {preview && <>
    <div className="entry-overlay"><div className="entry-content">
      {screen === 'landing' ? <section className="entry-intro" aria-labelledby="guide-title">
        <h1 id="guide-title">우소섬</h1><p>우소섬의 조개를 주워서 소원을 빌면<br/>그 소원이 이루어져요.</p>
        <button ref={entryButton} className="primary entry-button" onClick={() => setScreen('nickname')}>입장하기 <span aria-hidden="true">→</span></button>
      </section> : <section className="nickname-bubble" aria-labelledby="name-title">
        <h1 id="name-title">어떤 이름으로 만날까요?</h1><p>우소우소짱 머리 위에 이름이 떠요.</p>
        <form onSubmit={enterWorld} noValidate>
          <label htmlFor="nickname">닉네임</label>
          <input ref={nameInput} id="nickname" autoComplete="off" value={nickname} onChange={e => { setNickname(e.target.value); setNameError(''); }} maxLength={24} placeholder="1~12자" aria-invalid={!!nameError} aria-describedby={nameError ? 'name-error' : 'name-help'}/>
          {nameError ? <p id="name-error" className="form-error" role="alert">{nameError}</p> : <p id="name-help" className="input-help">함께 있는 사람들에게 보이는 이름이에요.</p>}
          <button className="primary entry-button" type="submit">우소섬으로 가기 <span aria-hidden="true">→</span></button>
        </form>
        <button className="text-button" onClick={() => { returning.current = true; setScreen('landing'); }}>돌아가기</button>
      </section>}
      <p className="landing-note">잠깐 쉬어 가도 괜찮아. 여기는 우소섬.</p>
      {sceneFailed && <p className="entry-error" role="status">해변을 불러오지 못했어요. <button className="text-button" onClick={restartAfterWorldError}>새로고침</button></p>}
    </div></div>
    </>}
    {screen === 'world' && !worldFailed && <><header className="hud">
      <div className="pocket-group">
        <button ref={bagButton} className="pocket-button" onClick={() => { setBag(!bag); setAudioOpen(false); }} aria-expanded={bag} aria-controls="pocket-panel" aria-label={`주머니 열기, 행운의조개 ${self?.count ?? 0}개`}>
          <Icon kind="bag"/><span>행운의조개</span><strong>{self?.count ?? 0}</strong>
        </button>
        {bag && <section id="pocket-panel" className="popover pocket-panel" aria-labelledby="pocket-title">
          <button className="icon-button close-button" aria-label="주머니 닫기" onClick={() => { setBag(false); bagButton.current?.focus(); }}><Icon kind="close"/></button>
          <h2 id="pocket-title">나의 주머니</h2><img src="/images/usousochan-sea-shell.png" alt="행운의조개"/>
          <p>모은 행운의조개 <strong>{self?.count ?? 0}개</strong></p>
          <p className="small-copy">지금 조개 {self?.picked.length ?? 0}/5개<br/>다섯 개를 모두 주우면 새 조개가 나타나요.<br/>페이지를 닫거나 새로고침하면 0개로 시작해요.</p>
        </section>}
      </div>
      <div className="sound-group">
        <button ref={audioButton} className="sound-button" onClick={() => { setAudioOpen(!audioOpen); setBag(false); }} aria-label="소리 설정" aria-expanded={audioOpen} aria-controls="audio-panel"><Icon kind={sound.muted ? 'muted' : 'sound'}/><span>소리</span></button>
        {audioOpen && <section id="audio-panel" className="popover audio-panel" aria-labelledby="audio-title">
          <h2 id="audio-title">해변의 소리</h2><button className="mute-button" onClick={sound.toggleMuted} aria-pressed={sound.muted}>{sound.muted ? '소리 켜기' : '전체 음소거'}</button>
          <label htmlFor="volume">음량 <span>{Math.round(sound.volume * 100)}%</span></label>
          <input id="volume" type="range" min="0" max="1" step="0.05" value={sound.volume} onChange={e => sound.setVolume(Number(e.target.value))}/>
          {sound.message && <p role="status" className="small-copy">{sound.message}</p>}
          {((!sound.playing && !sound.muted) || Boolean(sound.message)) && <button onClick={sound.start}>소리 다시 재생</button>}
        </section>}
      </div>
    </header>
    <div className="connection-status" role="status" aria-live="polite">
      {status === 'connecting' && <p>우소섬으로 가는 중…</p>}
      {status === 'reconnecting' && <p>다시 연결하고 있어요. 주머니는 잠시 보관해 둘게요.</p>}
      {(status === 'error' || status === 'disconnected') && <section className="connection-error"><h1>잠시 해변과 멀어졌어요</h1><p>{message}</p><button className="primary" onClick={exit}>다시 입장하기</button></section>}
    </div>
    {toast && <p className="toast" role="status">{toast}</p>}
    <footer className="action-area">
      {status === 'connected' && <>
        <p className="movement-help"><span className="desktop-help">클릭 · WASD · 방향키로 산책 / 누른 채 드래그로 시점 / E키로 행동</span><span className="mobile-help">짧게 터치해서 산책 · 누른 채 드래그해서 둘러봐요</span></p>
        <div className="action-buttons">
          {context.kind === 'collect' && <button className="primary" disabled={disabled} onClick={() => act()}>{pending !== null || pickLocked ? '조개를 줍는 중…' : '조개 줍기'}</button>}
          {context.kind === 'rest' && <button className="primary" disabled={disabled} onClick={() => act()}>눕기</button>}
          {context.kind === 'standUp' && <button className="primary" disabled={disabled} onClick={() => act()}>일어나기</button>}
        </div>
      </>}
    </footer></>}
  </main>;
}
